import { useEffect } from 'react';

import { DateTime } from 'luxon';
import {
  useUpdateGroupClassNoteMutation,
  useUpdateNoteMutation,
} from '~/store/services/notes/notes';
import { useClearableDebounce } from '~/lib/hooks/useClearableDebounce';

interface UseAutoSaveNoteProps {
  setHasUnsavedChanges: (changed: boolean) => void;
  setLastSavedTime: (lastSavedTime: DateTime) => void;
}

const useAutoSaveNote = ({
  setHasUnsavedChanges,
  setLastSavedTime,
}: UseAutoSaveNoteProps): {
  clearAutosaveDebounce: () => void;
  debounceFormChange: (func: () => void) => void;
  isNoteAutoSaving: boolean;
  noteAutoSavingError: string;
  resetNoteAutoSavingError: () => void;
  autoSaveNote: (body: UpdateNoteRequest) => void;
  autoSaveGroupClassNote: (body: UpdateGroupClassEventNoteRequest) => void;
} => {
  // Debounce hook to send form data to backend, if form fields are not updated for 5 seconds.
  const {
    clearDebounce: clearAutosaveDebounce,
    debouncedFn: debounceFormChange,
  } = useClearableDebounce();

  const [
    updateNote,
    {
      isSuccess: isUpdateNoteSuccess,
      isLoading: isUpdateNoteLoading,
      error: updateNoteError,
      reset: resetUpdateNoteError,
    },
  ] = useUpdateNoteMutation();
  const [
    updateGroupClassNote,
    {
      isSuccess: IsUpdateGroupClassNoteSuccess,
      isLoading: isUpdateGroupClassNoteLoading,
      error: updateGroupClassNoteError,
      reset: resetUpdateGroupClassNoteError,
    },
  ] = useUpdateGroupClassNoteMutation();

  // When auto save succeeds, set last saved time.
  useEffect(() => {
    if (isUpdateNoteSuccess || IsUpdateGroupClassNoteSuccess) {
      setLastSavedTime(DateTime.now());
    }
    setHasUnsavedChanges(false);
  }, [isUpdateNoteSuccess || IsUpdateGroupClassNoteSuccess]);

  let resetNoteAutoSavingError = null;
  if (updateNoteError) {
    resetNoteAutoSavingError = resetUpdateNoteError;
  } else if (updateGroupClassNoteError) {
    resetNoteAutoSavingError = resetUpdateGroupClassNoteError;
  }

  return {
    autoSaveGroupClassNote: (body: UpdateGroupClassEventNoteRequest) => {
      updateGroupClassNote({
        body,
        options: { skipInvalidatingGetNote: true },
      }).catch((error) => {
        console.error('Error in autoSaveGroupClassNote:', error);
      });
    },
    autoSaveNote: (body: UpdateNoteRequest) => {
      updateNote({
        body,
        options: { skipInvalidatingGetNote: true },
      }).catch((error) => {
        console.error('Error in autoSaveNote:', error);
      });
    },
    clearAutosaveDebounce,
    debounceFormChange,
    isNoteAutoSaving: isUpdateNoteLoading || isUpdateGroupClassNoteLoading,
    noteAutoSavingError:
      updateNoteError?.toString() ||
      updateGroupClassNoteError?.toString() ||
      '',
    resetNoteAutoSavingError,
  };
};

export default useAutoSaveNote;
